import { useState, useEffect } from 'react';
import { useMedia, NARROW, PHONE } from './media';
import { useBoundScope } from './keyScope';
import { useEscapeReleasesFields } from './keyboard';
import { useQuery, useQueryClient, useIsFetching } from '@tanstack/react-query';
import { getMe, getHealth, getToken, clearToken, logout, acceptInvitation, ApiError } from './api';
import { TaskList, aimAtProjectBoard, aimAtLate, aimAtParked } from './components/TaskList';
import { GoalTree } from './components/GoalTree';
import { GoalDetail } from './components/GoalDetail';
import { TaskDetail } from './components/TaskDetail';
import { TaskView } from './components/TaskView';
import { Templates } from './components/Templates';
import { Settings } from './components/Settings';
import { Habits } from './components/Habits';
import { Today } from './components/Today';
import { Labels } from './components/Labels';
import { Reflection } from './components/Reflection';
import { NotificationBell } from './components/NotificationBell';
import { GlobalSearch } from './components/GlobalSearch';
import { Shortcuts } from './components/Shortcuts';
import { Now } from './components/Now';
import { SignIn, SignInError, invitationTokenFromUrl } from './components/SignIn';
import { Consent, consentRequestFromUrl } from './components/Consent';
import { CaptureDisclosure } from './components/CaptureDisclosure';
import { MoreMenu } from './components/MoreMenu';
import { Projects } from './components/Projects';
import { Week } from './components/Week';
import { DayPage } from './components/DayPage';
import { todayInZone } from './timezones';

type Screen =
  | 'today'
  /*
    The day's page. A screen with no tab, like `Now` — it is *about* a particular day, so it is
    reached from the date row that names one and never from a tab that would have to invent its
    own. `SECONDARY` is a list of places; this is a place you go from somewhere.
  */
  | 'page'
  | 'week'
  | 'tasks'
  | 'goals'
  | 'projects'
  | 'habits'
  | 'labels'
  | 'reflection'
  | 'templates'
  | 'settings';

/**
 * The two that stay, and the rest.
 *
 * A tab per screen, plus a wordmark, a capture box, search, a bell and a shortcuts button,
 * wrapped onto two lines on a laptop and three on a phone — so the header cost more vertical
 * space than the first two rows of whatever it was heading. The owner's cut: Today and Tasks are
 * what you press repeatedly; every other screen is a place you go deliberately, and a deliberate
 * trip can afford one extra click.
 */
const PRIMARY: { id: Screen; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'tasks', label: 'Tasks' },
];

const SECONDARY: { id: Screen; label: string }[] = [
  /*
    The week canvas: Today at one zoom out.

    Here rather than beside Today, and as a screen rather than as a toggle on Today's date row,
    on purpose. `PRIMARY` is two tabs by an owner decision — "Today and Tasks are what you press
    repeatedly" — and a third would be reversing it. And the *toggle* is the job of
    `01a08883-a3ea` ("Plan door: Today with the week canvas as its other zoom, one toggle, one
    saved zoom level"), which is blocked on this task: building it here would be building the
    door before the room, and it would have to be unpicked. `SCREEN_KEY` already remembers which
    screen you were on, so the zoom does survive a reload in the meantime.
  */
  { id: 'week', label: 'Week' },
  { id: 'goals', label: 'Goals' },
  /*
    Back after migration 0019 retired it.

    `Projects.tsx` and `Members.tsx` were left in the tree, imported by nothing, for the whole of
    the sharing work — so membership, roles and invitations were built, tested and mounted with no
    way to reach any of it. This is the entry that makes the rest of Phase 17 visible.
  */
  { id: 'projects', label: 'Projects' },
  { id: 'habits', label: 'Habits' },
  { id: 'labels', label: 'Labels' },
  { id: 'reflection', label: 'Reflection' },
  { id: 'templates', label: 'Templates' },
  { id: 'settings', label: 'Settings' },
];

/**
 * The screens the task quick view is a panel *beside* rather than a panel *over*.
 *
 * `goTo` leaves the quick view open on these and clears it everywhere else, and the reason is the
 * one in its own comment: the panel is "a panel beside a list that is still on screen, not a
 * different place". These two render that list — Tasks is the list, Today is the shortlist — so the
 * tab press has somewhere visible to land with the panel still up.
 *
 * `week` is deliberately not here, even though its cards open tasks into this panel. It is a canvas
 * of seven columns rather than a column of rows, and it is already one of the two screens allowed
 * past the 60rem reading cap for that reason (see `shell`); taking 384px off it puts the columns
 * back at the ~118px that cap exists to avoid. A card clicked after arriving still opens the panel,
 * which is the gesture somebody actually asked for.
 *
 * Named rather than inlined as `next === 'tasks' || next === 'today'` so that the question a new
 * screen has to answer — does it put a list beside the panel? — is written down where the screen
 * gets added.
 */
const PANEL_BESIDE_A_LIST = new Set<Screen>(['tasks', 'today']);

const SCREEN_KEY = 'planner.screen';

/** The screen you were last on, if it is still a screen. */
function storedScreen(): Screen {
  const saved = localStorage.getItem(SCREEN_KEY);
  const known = [...PRIMARY, ...SECONDARY].some((s) => s.id === saved);
  return known ? (saved as Screen) : 'today';
}

function screenTitle(screen: Screen): string {
  return [...PRIMARY, ...SECONDARY].find((s) => s.id === screen)?.label ?? 'Planner';
}

export function App() {
  const queryClient = useQueryClient();
  /*
    Escape gives the keyboard back, whatever screen you are on and whatever field has it.

    Here rather than on a screen because the field it releases is usually the header's — see
    `useEscapeReleasesFields`, which also sets out why it listens in the capture phase.
  */
  useEscapeReleasesFields();
  // A session cookie is invisible to script, so "am I signed in" is only answerable by
  // asking the server — which is why `/api/me` runs unconditionally below rather than
  // waiting for a stored token the way it did before sessions existed.
  const [, setTokenAttempt] = useState(() => getToken() !== null);
  /*
    The last screen, remembered.

    Every reload — and every deploy — put you back on Today regardless of what you were doing.
    `localStorage` rather than the URL because the app has no router; this is the smallest thing
    that stops a refresh from being a navigation.
  */
  const [screen, setScreenState] = useState<Screen>(storedScreen);
  const setScreen = (next: Screen) => {
    setScreenState(next);
    localStorage.setItem(SCREEN_KEY, next);
  };
  /**
   * A day the week canvas sent us to, or null for the account's own today.
   *
   * Not stored beside `screen`: arriving on a Tuesday in October is a navigation, not a
   * preference, and remembering it across a reload would mean the app opened on a day nobody
   * asked for. Cleared whenever the Today tab is pressed — see `goTo`.
   */
  const [dayDate, setDayDate] = useState<string | null>(null);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  /*
    Which way a task opens.

    On a desktop, clicking a task always opens the side panel — the quick view. It keeps the list
    visible and holds your scroll position, and it is what a click on a row has always meant here.
    The full read view is somewhere you ask to go: the panel's own control, "Open full" in the row
    menu, or shift-Enter on the cursor.

    This was briefly remembered per browser, so choosing the full view once quietly changed what
    every later click did. A preference that silently redefines the primary gesture is worse than
    no preference: the click stops being predictable, and the list you were working through
    disappears when you did not ask it to.

    Below the two-column breakpoint the full view is not a choice at all. The panel renders there
    as the last block of a very tall page, so tapping a task looked like nothing happening.

    Which is why this is three states rather than a boolean. It was `fullView: boolean` with
    `showFull = fullView || narrow`, and the read view's **Edit** only cleared `fullView` — so at
    375 the two disagreed, `showFull` stayed true, and Edit did nothing at all. The panel is the
    only place a task's date, labels and goals can be changed, so on a phone the app had no way to
    edit a task; the bulk-edit popover was the sole route to scheduling, which is a lot of weight
    for a control that was itself drawn off the side of the screen.

    `auto` is the default and is what the breakpoint decides. `panel` and `full` are answers to a
    request somebody made, and a request beats a breakpoint: asking for the form on a phone gets
    the form. Every plain click resets to `auto`, so the choice never outlives the task it was
    made about — the per-browser memory this used to have redefined the primary gesture silently.
  */
  const [taskView, setTaskView] = useState<'auto' | 'panel' | 'full'>('auto');
  const narrow = useMedia(NARROW);
  /* See `PHONE`: the Tasks screen's inline capture is hidden at this width, so the header's
     `+` has to take over — otherwise the screen has no way to capture anything at all. */
  const phone = useMedia(PHONE);
  const showFull = openTaskId !== null && (taskView === 'full' || (taskView === 'auto' && narrow));
  /*
    A panel asked for below the breakpoint takes the whole pane, rather than stacking under the
    list it came from. That stacking is the original reason narrow forces the read view: the panel
    rendered thousands of pixels down a very tall page, so opening it looked like nothing
    happening. Hiding the screen behind it makes "asked for" and "arrived somewhere" the same act.
  */
  const detailOnly = openTaskId !== null && !showFull && narrow;
  /**
   * The block Now is showing, when Now has the pane. Null means Now is not open.
   *
   * A state beside `screen` rather than a tenth `Screen`, for the reason the read view is one: it
   * is somewhere you go *from* the day with a particular block in hand, and it has no nav tab —
   * the tab is `01a08883-c0b8`, which is blocked on this and would collide with an entry added
   * here. `''` is "open, holding nothing", which is what a day with no blocks gives you.
   */
  const [nowBlockId, setNowBlockId] = useState<string | null>(null);
  /** Whether the current screen gets the main pane, or a task view — or Now — has taken it. */
  const screenVisible = !showFull && !detailOnly && nowBlockId === null;
  /** A plain click: the quick view, every time. */
  const openTaskPanel = (id: string) => {
    setTaskView('auto');
    setOpenTaskId(id);
  };
  /** Asked for explicitly. */
  const openTaskFull = (id: string) => {
    setTaskView('full');
    setOpenTaskId(id);
  };
  /** Also asked for explicitly, which is why it wins over the breakpoint. */
  const backToPanel = () => setTaskView('panel');
  /** Which goal's own screen is open, if any. Cleared when the Goals tab is chosen — see `goTo`. */
  const [openGoalId, setOpenGoalId] = useState<string | null>(null);
  /* Whether the keys panel is showing. Held here because on a phone the way in is the more menu
     rather than the `?`, which has no room in the header at that width. */
  const [help, setHelp] = useState(false);
  /** The screen whose keys are actually bound right now — see the note beside `<Shortcuts>`. */
  const boundScope = useBoundScope();
  /** Which habit a notification or a search result asked for, so the screen can open it. */
  const [openHabitId, setOpenHabitId] = useState<string | null>(null);
  /** Set when a project is reached from global search, so the screen opens it rather than the list. */
  const [searchedProjectId, setSearchedProjectId] = useState<string | null>(null);
  /** Bumped to remount Tasks when something aims it from outside while it is open — see the bell. */
  const [tasksArrival, setTasksArrival] = useState(0);

  /*
    Choosing a tab shows you that screen, including when you are already on it.

    The comment on `openGoalId` said it was "cleared when the Goals tab is chosen afresh" and it
    was not: the primary nav called `setScreen` alone, and `openGoalId` only ever got cleared by
    the *more* menu, which does not contain Goals. So with a goal's own page open, pressing Goals
    did nothing at all — the tab is the control most people reach for and it was inert. The way
    back was the "← All goals" link, which is fine and is not the only thing anyone will try.

    Two other screens have a detail state of the same kind and get the same treatment: a project
    reached from search, and a task opened in the full read view.

    The *quick view* is not cleared on the two screens that argue for keeping it. It is a panel
    beside a list that is still on screen, not a different place — closing it on a tab press would
    take away something the tab press did not ask about. Escape closes it, as it always has.

    That argument has a premise, and the premise fails elsewhere: on Goals, Habits, Labels,
    Templates, Reflection and Settings there is no list the panel sat beside, so what survives the
    tab press is a 384px column pinned over the screen you did ask for. See `PANEL_BESIDE_A_LIST`.
  */
  const goTo = (next: Screen, day: string | null = null) => {
    setScreen(next);
    /*
      Which day Today opens on, decided here rather than by ordering two setters.

      Pressing the Today tab is `goTo('today')` and lands on the account's own today; the week
      canvas's card is `goTo('today', date)` and lands on that one. Written as a parameter because
      the alternative — clearing it in this function and setting it again at the call site — is
      two queued updates whose result depends on which was called last, which is exactly the kind
      of thing a later tidy-up reorders without noticing.
    */
    // The page is about a day too, so it keeps the one it was asked for — same parameter, one more
    // screen that means something by it.
    setDayDate(next === 'today' || next === 'page' ? day : null);
    /* Now has no tab, so any tab press is a request to be somewhere else — the same treatment
       the full read view and a searched project get above. */
    setNowBlockId(null);
    /* A habit asked for by a notification is a one-time request: coming back to Habits later
       should show the list, not re-open whatever the bell last pointed at. */
    if (next !== 'habits') setOpenHabitId(null);
    if (next === 'goals') setOpenGoalId(null);
    if (next === 'projects') setSearchedProjectId(null);
    if (next === 'tasks' || next === 'today') setTaskView('auto');
    /*
      And below the breakpoint the open task *is* the pane, so leaving it open would make a tab
      press a navigation with nothing to show for it — the same complaint as `openGoalId` above.
      Beside a list on a desktop the quick view is still left alone, because there the tab press
      has somewhere visible to land without taking the panel away.

      The second arm is the other half of that same sentence: *beside a list* is a condition, not a
      description of every screen. Arriving somewhere that renders no such list leaves the panel
      with nothing to be beside, which is the state a persona round measured — a task opened on
      Tasks still pinned right over the goal tree, every title wrapped to three lines, under a
      heading too narrow to say which task it was.

      Only navigation clears it. Opening a task *from* one of these screens — `onOpenTask` on the
      goal tree, the project board, a template, or a search hit — happens after arrival and is
      left alone, because that panel is one somebody asked for here.
    */
    if (narrow || !PANEL_BESIDE_A_LIST.has(next)) setOpenTaskId(null);
  };

  /*
    The consent page, which is a place rather than a screen.

    `/oauth/consent?request=…` is where `GET /api/oauth/authorize` sends the browser, and it is the
    one path in this application that arrives from outside with something to say. Read once, at
    mount, rather than kept in state: the URL does not change while the page is open, and both
    answers navigate away to the client's own callback.

    A `Screen` would have been wrong. Every entry in `PRIMARY` and `SECONDARY` is somewhere you can
    get back to from the nav, and `SCREEN_KEY` remembers the last one — so this would have been
    restored on the next reload, asking about a request that expired ten minutes ago. This replaces
    the workspace instead, behind the same sign-in gate, which is what makes an unconnected client's
    link land on the sign-in screen and then on the question.
  */
  const [consentRequest] = useState(consentRequestFromUrl);

  const health = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    refetchInterval: 30_000,
    // See `workspaceLoading` below: a query that polls is never evidence that the workspace is
    // waiting for anything.
    meta: { polls: true },
  });
  // Always asked: the answer decides whether a cookie signed us in.
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });

  /*
    The account this page load belongs to, and a reload if `me` stops naming it.

    Every sign-in and sign-out reloads its own tab, so within one load the account is fixed —
    except that the session is a cookie the whole browser shares and `me` refetches on focus. A
    second tab signing in as somebody else therefore hands this tab a new `me` while every other
    query in the cache is still the previous account's. Measured on the Tasks scope
    (`scopeAcrossAccounts`, "another tab…"): the list judged the new account's remembered project
    against the old account's project list, found it missing, and overwrote it with `mine`.
    Re-keying TaskList alone left that race in place (1 of 10 runs), because the cache it reads
    from was the stale part.

    So the workspace is not rendered for an account it was not loaded as, and the tab reloads —
    what sign-out already does, for the same reason: discarding all client state is what a
    change of account means.
  */
  const [loadedAs, setLoadedAs] = useState<string | null>(null);
  useEffect(() => {
    if (me.data && loadedAs === null) setLoadedAs(me.data.id);
  }, [me.data, loadedAs]);
  const accountChanged = loadedAs !== null && me.data !== undefined && me.data.id !== loadedAs;
  useEffect(() => {
    if (accountChanged) window.location.reload();
  }, [accountChanged]);

  /*
    An invitation link opened while already signed in.

    `acceptInvitationsFor` had two callers — registration and Google login — so an account that
    already had a password could never accept one: the link was valid, the token was valid, and
    nothing consumed it. Sharing had no door. The sign-in screen has always read the token off the
    URL for somebody signed *out*; this is the other half.

    Runs once per token: the accept is idempotent server-side, but the URL is rewritten so a
    refresh does not look like an invitation again.
  */
  const [invitationDone, setInvitationDone] = useState(false);
  useEffect(() => {
    const token = invitationTokenFromUrl();
    if (!token || !me.data || invitationDone) return;
    setInvitationDone(true);
    void acceptInvitation(token)
      .then(() => {
        void queryClient.invalidateQueries();
      })
      .catch(() => {
        /* An expired or withdrawn link is not an error worth interrupting the app for — the
           Projects screen simply will not show it. */
      })
      .finally(() => window.history.replaceState(null, '', '/'));
  }, [me.data, invitationDone, queryClient]);

  // `isSuccess`, not `data !== undefined`: React Query keeps the last successful data
  // alongside a later error, so a signed-out user would otherwise keep their workspace
  // on screen until something else forced a render.
  const signedIn = me.isSuccess;
  const unauthenticated = me.error instanceof ApiError && me.error.status === 401;

  const signOut = async () => {
    // Both credentials, because either could be what got us in.
    await logout().catch(() => undefined);
    clearToken();
    setTokenAttempt(false);
    setOpenTaskId(null);
    queryClient.clear();
    // Then reload. Sign-out is the one moment where discarding *all* client state is
    // exactly what is meant, and clearing the cache alone leaves observers holding the
    // data they last saw — which showed the signed-out user their workspace.
    window.location.assign('/');
  };

  /*
    The day screen is allowed to be wider than the rest.

    Everything else is capped at 60rem because it is text — a task list, a goal tree, prose — and
    past about 50 characters a line stops being scannable. The timeline is not text: it is a grid
    whose blocks split its width between them, so at 960px two concurrent blocks got 244px each and
    three got 163px, which truncates a title at roughly twenty characters. Width is the only thing
    that fixes that, and the cap keeps it from becoming a 2000px sweep on a large monitor.
  */
  /* The week canvas needs the day screen's extra width for the same reason the day does: it is a
     canvas of columns rather than a column of rows, and at the reading width the seven columns
     were 118px each. */
  const shell = [
    screen === 'today' || screen === 'week' ? 'day' : '',
    openTaskId ? 'with-detail' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /*
    Whether the workspace is waiting for data — `aria-busy` on the region below the header.

    Nothing said this before. A screen whose first query has not answered renders its own shape
    with nothing in it: the Tasks list is a toolbar above an empty column, and neither the row
    list nor the "nothing here" line is drawn, because both are keyed off data that has not
    arrived. To a screen reader that is a region that silently changed and now says nothing,
    which is the same class of gap `QueryState` exists to close for the screens that do use it.

    Queries that poll on a timer are excluded. The health pill refetches every 30 seconds and the
    notification bell every 60, both into the *header*, so counting them would flip the
    workspace's `aria-busy` twice a minute for work that changes nothing inside it — and
    `aria-busy` is a claim about this region, not about the tab. They mark themselves with
    `meta.polls` rather than being listed here, so the next timer query opts itself out where it
    is written instead of needing an edit in this file that nobody will remember to make.

    `e2e/fixtures.ts`'s `goTo` reads this, and the ordering that makes it safe to read is measured
    there.
  */
  const workspaceLoading = useIsFetching({ predicate: (q) => q.meta?.polls !== true }) > 0;

  return (
    <main className={shell}>
      <header>
        {/*
          The wordmark is gone.

          It named the app to somebody who had already opened it, on every screen, forever. What
          replaces it is an `h1` naming the screen you are on — visually hidden, because the screen
          announces itself perfectly well through the highlighted tab, but a document with no
          top-level heading is a real loss for anyone navigating by headings.
        */}
        <h1 className="sr-only">
          {consentRequest === null ? screenTitle(screen) : 'Connect an application'}
        </h1>
        {/*
          No nav while a consent question is open.

          The tabs would still be live and would still set `screen`, but the consent page has taken
          the pane — so pressing one would do nothing visible, which is the exact complaint `goTo`'s
          comment records about the Goals tab. There is nowhere to go from here anyway: both answers
          leave for the client's own callback, and the way out without answering is the back button.
        */}
        {me.data && consentRequest === null && (
          <nav>
            {PRIMARY.map((s) => (
              <button
                key={s.id}
                className={screen === s.id ? 'tab active' : 'tab'}
                aria-current={screen === s.id ? 'page' : undefined}
                onClick={() => goTo(s.id)}
              >
                {s.label}
              </button>
            ))}

            <MoreMenu
              screen={screen}
              items={SECONDARY}
              onPick={goTo}
              /* On a phone the `?` has no room in the header — see `extra` on `MoreMenu`. */
              {...(phone
                ? { extra: { label: 'Keys and shortcuts', onPick: () => setHelp(true) } }
                : {})}
            />

            {/*
              Capture, from every screen, behind a `+`.

              It lived on the Tasks screen alone, so a thought had to survive a tab click — and
              Today, the default screen, had no way to add anything at all. `c` focuses it, the
              same treatment `/` gives search.

              Phase 66 gave capture a facet row (goal, labels, priority) which is right on the
              Tasks screen and wrong here: always-visible, it added a second line to the header on
              every screen, which is the exact cost this phase exists to remove. So the header
              carries a `+` that discloses the box, and `c` opens it as well as focusing it.

              Not on the Tasks screen, which has the full version inline. Two identical inputs on
              one screen is worse than one in the wrong place.
            */}
            {/*
              The utilities, in their own group.

              `margin-left: auto` used to sit on `nav > .capture`, which stopped matching anything
              when capture went behind its `+` and became `.capture-disclosure` — and which was
              never on the Tasks screen at all, where the header has no capture. So the icons
              simply followed the tabs on the left instead of sitting at the right end. A wrapper
              that always exists is the thing to push, rather than whichever control happens to be
              first today.
            */}
            <div className="nav-utils">
              {(screen !== 'tasks' || phone) && <CaptureDisclosure />}
              <GlobalSearch
                onOpenTask={openTaskPanel}
                /* Was `labels`, from when a project *was* a label. It is a project again. */
                onOpenLabels={() => setScreen('labels')}
                onOpenHabits={(id) => {
                  goTo('habits');
                  setOpenHabitId(id ?? null);
                }}
                onOpenProjects={(projectId) => {
                  setSearchedProjectId(projectId ?? null);
                  setScreen('projects');
                }}
                onOpenGoal={(id) => {
                  setOpenGoalId(id);
                  setScreen('goals');
                }}
              />
              <NotificationBell
                onOpenTask={openTaskPanel}
                onOpenHabits={(id) => {
                  goTo('habits');
                  setOpenHabitId(id ?? null);
                }}
                onOpenGoals={() => setScreen('goals')}
                /* The dormancy digest opens the shelf. The bell is in the header, so Tasks may
                   already be mounted — and an aim is read only on mount, which would leave it
                   sitting in storage to open the shelf unasked on the next visit. Bumping the
                   list's key remounts it, so the aim is read now. */
                onOpenParked={() => {
                  aimAtParked(me.data.id);
                  setTasksArrival((n) => n + 1);
                  goTo('tasks');
                }}
                /* Accepting an invitation is the one notification that *creates* something to
                   look at, so it lands on the project it just made visible. */
                onJoinedProject={(projectId) => {
                  setSearchedProjectId(projectId);
                  setScreen('projects');
                }}
              />
              {/*
                The panel shows `Anywhere` plus the keys for the screen you are on — see `keys.ts`,
                which the task list's own hint reads too.

                Which screen that is comes from whichever component is *listening*, not from this
                file's own idea of where you are. It used to be computed here, and got it wrong
                twice: it read the Tasks screen's view out of `localStorage` during render to guess
                list-versus-board, and it had no answer at all for the full read view, which took
                the pane while `screen` still said `tasks` — so `?` there printed the whole of the
                list's group, none of which that screen binds. See `keyScope.ts`.
              */}
              <Shortcuts screen={boundScope} open={help} onOpenChange={setHelp} />
            </div>
          </nav>
        )}
        {/*
          The health pill, only when there is something wrong.

          `api 0.1.0 · db up` was printed on every screen at all times — 25 pixels of a 208-pixel
          header telling you the thing you are already using is working. The version belongs in
          Settings, which shows it; what actually needs to interrupt you is the failure, so that is
          all this renders now.
        */}
        {!health.isPending && health.data?.database !== 'up' && (
          <span className="pill bad" role="status">
            {health.data ? `db ${health.data.database}` : 'api unreachable'}
          </span>
        )}
      </header>

      {me.isPending && <p className="muted">Loading your account…</p>}

      {unauthenticated && (
        <SignIn
          onTokenSaved={() => {
            setTokenAttempt(true);
            void queryClient.invalidateQueries();
          }}
        />
      )}

      {me.error instanceof ApiError && !unauthenticated && (
        <SignInError error={me.error} onReset={() => void signOut()} />
      )}

      {signedIn && me.data && consentRequest !== null && <Consent requestId={consentRequest} />}

      {signedIn && me.data && !accountChanged && consentRequest === null && (
        <div
          className={detailOnly ? 'workspace detail-only' : 'workspace'}
          aria-busy={workspaceLoading}
        >
          <div className="main-pane">
            {/*
              The read view takes the whole pane rather than sitting beside the list: it exists
              because the panel is too narrow to read a description in, so giving it the panel's
              width would repeat the problem.
            */}
            {showFull && openTaskId !== null && (
              <TaskView
                taskId={openTaskId}
                /*
                  Back closes the task and leaves the preference alone: choosing the full view once
                  should keep tasks opening that way until you choose otherwise, and the way you
                  choose otherwise is Edit, which is an explicit request for the form.
                */
                onBack={() => setOpenTaskId(null)}
                onEdit={() => {
                  backToPanel();
                }}
                onOpenTask={openTaskPanel}
              />
            )}
            {nowBlockId !== null && !showFull && !detailOnly && (
              <Now
                onBack={() => setNowBlockId(null)}
                onOpenTask={openTaskPanel}
                fromBlockId={nowBlockId === '' ? null : nowBlockId}
              />
            )}
            {screenVisible && screen === 'today' && (
              <Today
                /*
                  Keyed on the day the week canvas asked for, so choosing one remounts Today on it.

                  `Today` reads its date into `useState` once, which is right for the screen's own
                  arrows and wrong for arriving from somewhere else — a changed prop would be
                  ignored. A key is the smallest honest way to say "this is a different day's
                  screen"; the alternative, an effect that writes the date back into state, has to
                  decide what to do when the user has already paged away from it.
                */
                key={dayDate ?? 'today'}
                {...(dayDate ? { initialDate: dayDate } : {})}
                onOpenTask={openTaskPanel}
                onOpenNow={(blockId) => setNowBlockId(blockId)}
                timezone={me.data.timezone}
                dayWindow={me.data.dayWindow}
                /* The Tasks screen, already aimed at that project's board — the screen the
                   pointer is pointing at, rather than the screen it is on. */
                onOpenProjectBoard={(projectId) => {
                  aimAtProjectBoard(me.data.id, projectId);
                  goTo('tasks');
                }}
                /* Same shape, same reason: the aim is written before the screen that reads it
                   mounts. `aimAtLate` sets the scope too — see its comment. */
                onSeeLate={() => {
                  aimAtLate(me.data.id);
                  goTo('tasks');
                }}
                onOpenPage={(date) => goTo('page', date)}
              />
            )}
            {screenVisible && screen === 'page' && (
              <DayPage
                date={dayDate ?? todayInZone(me.data.timezone)}
                timezone={me.data.timezone}
                /* Stepping a day stays on the page rather than bouncing through `goTo`, so the
                   arrows change the date and nothing else about where you are. */
                onChangeDate={(day) => setDayDate(day)}
              />
            )}
            {screenVisible && screen === 'week' && (
              <Week
                onOpenTask={openTaskPanel}
                /* The phone's way to an hour: Today for that date, the screen that already owns
                   hour precision. See `Week.tsx`'s note on the stacked layout. */
                onOpenDay={(date) => goTo('today', date)}
              />
            )}
            {screenVisible && screen === 'tasks' && (
              <TaskList
                key={tasksArrival}
                accountId={me.data.id}
                onOpen={openTaskPanel}
                onOpenFull={openTaskFull}
                /* So the keyboard cursor sits on whatever the panel is showing — the app kept two
                   notions of "the task I am acting on" and a mouse click made them disagree. */
                openTaskId={openTaskId}
              />
            )}
            {screenVisible &&
              screen === 'goals' &&
              (openGoalId ? (
                <GoalDetail
                  goalId={openGoalId}
                  onOpenTask={openTaskPanel}
                  onOpenGoal={setOpenGoalId}
                  onClose={() => setOpenGoalId(null)}
                />
              ) : (
                <GoalTree onOpenTask={openTaskPanel} onOpenGoal={setOpenGoalId} />
              ))}
            {screenVisible && screen === 'projects' && (
              <Projects openProjectId={searchedProjectId} onOpenTask={openTaskPanel} />
            )}
            {screenVisible && screen === 'habits' && (
              <Habits openHabitId={openHabitId} timezone={me.data.timezone} />
            )}
            {screenVisible && screen === 'labels' && <Labels />}
            {screenVisible && screen === 'reflection' && <Reflection />}
            {screenVisible && screen === 'templates' && <Templates onOpenTask={openTaskPanel} />}
            {screenVisible && screen === 'settings' && <Settings />}
          </div>
          {openTaskId && !showFull && (
            <TaskDetail
              taskId={openTaskId}
              onClose={() => setOpenTaskId(null)}
              onOpenTask={openTaskPanel}
              onExpand={() => openTaskFull(openTaskId)}
            />
          )}
        </div>
      )}

      {me.data && (
        <footer className="muted">
          {me.data.email} · {me.data.timezone} ·{' '}
          {/* A session can now come from Google *or* a password, so it no longer names
              a provider it cannot know. A token is distinguishable and worth flagging,
              because it is a credential the user pasted rather than one they logged in
              with. */}
          {me.data.authenticatedVia === 'token' ? 'signed in with an API token' : 'signed in'} ·{' '}
          <button className="link" onClick={() => void signOut()}>
            sign out
          </button>
        </footer>
      )}
    </main>
  );
}
